/* eslint-disable semi */
import * as authSelectors from '@redux/reducers/auth';
import L from 'leaflet';
import moment from 'moment';
import { useRef } from 'react';
import { Square } from 'react-feather';
import { IntlShape } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';
import Icon from 'utility/icomoon';
import { isNaNOrNullable } from 'utility/Utils';
import { Schedule } from 'views/screens/Schedules/views/listTrigger';
import { getPerformanceColor, getPerformanceTitle } from 'views/screens/Telemetry/utils';
import {
  getDatesDifferenceString,
  GetMainStateById,
  GetMainStateName
} from 'views/screens/Units/utils';
import { env } from '../../../../env';
import { appIntl } from '../../../../utility/context/IntlGlobalProvider';
import { AssignedUnit } from '../hooks/useGetAssignedUnits';
import {
  Assignment,
  AssignmentFormValues,
  AssignmentTypes,
  UnitsRoutesAssignment
} from '../views/RoutesAssignUnitScreen/RoutesAssignUnitContext';

// Legacy type aliases for backward compatibility
export type AssignmentByDate = Assignment;
export type AssignmentBySchedule = Assignment;

// `nowAnchor` fija el instante contra el que se calculan los campos relativos
// (el "hace X" del estado principal y el texto del ETA). Sin él se usa el reloj,
// y una pantalla que reevalúe este mapeo en cada render obtiene filas distintas
// cada minuto: el grid detecta el cambio, se refresca en medio de un clic y la
// selección del usuario se pierde. Quien pinte estos datos en una tabla debe
// anclarlo a la respuesta del backend.
export const getRouteExecutionInformation = (
  routeExecution,
  parsers,
  authSystems,
  nowAnchor?: number | string | null
) => {
  const intl = appIntl();
  const now = () => (nowAnchor ? moment(nowAnchor) : moment());

  if (!routeExecution || !parsers || !authSystems) {
    console.warn('getRouteExecutionInformation: Missing required parameters');
    return undefined;
  }

  try {
    const { routeStatusLabel, routeStatusLabelInfo } = getRouteExecutionStatusLabel(
      routeExecution.routeStatus,
      routeExecution.completedReason
    );

    const speedFormat =
      parsers
        .convertKilometersPerHourToAuthUserSpeedSystem(
          routeExecution.speed < 0 ? 0 : routeExecution.speed
        )
        .toFixed(2) +
      ' ' +
      authSystems.speed;

    const mainState = GetMainStateById(routeExecution.mainStateId);

    // Ensure routeProgress is always a valid number
    const routeProgress = isNaN(routeExecution.routeProgress)
      ? 0
      : Math.round(routeExecution.routeProgress);

    return {
      ...routeExecution,
      mainStateId: routeExecution.mainStateId,
      mainStateName: GetMainStateName(
        mainState,
        intl.formatMessage({ id: mainState.intl }),
        routeExecution.mainStateDate,
        speedFormat,
        routeExecution.downtimeReasonDescription,
        moment(routeExecution.mainStateDate).from(now())
      ),
      routeStatusLabel: routeStatusLabel || 'N/A',
      routeStatusLabelInfo: routeStatusLabelInfo || '',
      routeProgress: routeProgress,
      completionTypeLabel: intl.formatMessage({
        id: completionTypes[routeExecution.completionType]?.label || 'common.unknown'
      }),
      isDeviceOpenLabel: intl.formatMessage({
        id: routeExecution.isDeviceOpen
          ? 'trackingUnits.sat.open'
          : routeExecution.isDeviceOpen == null
            ? 'trackingUnits.sat.unknown'
            : 'trackingUnits.sat.closed'
      }),
      stopStatus: !routeExecution.stopStatus,
      speedLabel: parsers.convertKilometersPerHourToAuthUserSpeedSystem(routeExecution.speed || 0),
      routeProgressLabel: routeProgress + '%',
      distanceToCheckpointLabel:
        Math.round(
          parsers.convertMetersToAuthUserDistanceSystem(routeExecution.distanceToCheckpoint || 0)
        ) +
        ' ' +
        authSystems.distance,
      distanceLabel:
        Math.round(parsers.convertMetersToAuthUserDistanceSystem(routeExecution.distance || 0)) +
        ' ' +
        authSystems.distance,
      fuelConsumption: parsers.convertLitersToAuthUserCapacitySystem(
        routeExecution.fuelConsumption || 0
      ),
      expectedPerformance: parsers.convertKilometersPerLiterToAuthUserFuelSystem(
        routeExecution.expectedPerformance || 0
      ),
      performanceOnRoute: parsers.convertKilometersPerLiterToAuthUserFuelSystem(
        routeExecution.performanceOnRoute || 0
      ),
      distanceProgress: parsers.convertKilometersToAuthUserDistanceSystem(
        routeExecution.distanceProgress || 0
      ),
      auditCreateSourceLabel:
        [
          routeExecution.auditCreateSourceId === 2
            ? intl.formatMessage({ id: 'routes.createSourceByAPIMessage' })
            : '',
          routeExecution.hasIncompleteCheckpoints === 1
            ? intl.formatMessage({ id: 'routes.hasIncompleteCheckpointsMessage' })
            : ''
        ]
          .filter(Boolean)
          .join(' | ') || intl.formatMessage({ id: 'routes.createSourceCorrectMessage' }),
      // ETA: usar dinámica si la ruta ya salió de origen, estática si aún está en origen o programada
      // Nota: camelcase-keys convierte PredictedETA → predictedEta (acronym lowercase)
      // No mostrar si la ruta está en destino (2), cancelada (8) o finalizada (6)
      estimatedArrival: (() => {
        if (!routeExecution.usePredictedEta) return null;
        if ([2, 6, 8].includes(routeExecution.routeStatus)) return null;
        const atOriginStatuses = [1, 15, 18]; // en origen, EAO, programada
        const hasLeftOrigin = !atOriginStatuses.includes(routeExecution.routeStatus);
        return hasLeftOrigin ? routeExecution.predictedDynamicEta : routeExecution.predictedEta;
      })(),
      estimatedArrivalTooltip: (() => {
        if (!routeExecution.usePredictedEta) return null;
        if ([2, 6, 8].includes(routeExecution.routeStatus)) return null;
        const atOriginStatuses = [1, 15, 18];
        const eta = !atOriginStatuses.includes(routeExecution.routeStatus)
          ? routeExecution.predictedDynamicEta
          : routeExecution.predictedEta;
        if (!eta) return null;
        const etaMoment = moment(eta);
        const abs = Math.abs(etaMoment.diff(now(), 'minutes'));
        const isPast = etaMoment.isBefore(now());
        const h = Math.floor(abs / 60);
        const m = abs % 60;
        const duration = h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
        const relative = isPast ? `hace ${duration}` : `en ${duration}`;
        return `${etaMoment.format('YYYY-MM-DD HH:mm')} · ${relative}`;
      })()
    };
  } catch (error) {
    console.error('Error processing route execution information:', error, {
      routeId: routeExecution?.routeId,
      routeAssignmentId: routeExecution?.routeAssignmentId
    });
    return routeExecution; // Return original data if processing fails
  }
};

// Debug helper: log route executions missing mainStateName
export const logMissingMainState = (routeExecution) => {
  try {
    const id = routeExecution?.routeAssignmentId ?? routeExecution?.routeId;
    if (!routeExecution?.mainStateName) {
      // eslint-disable-next-line no-console
      console.debug('RouteExecution missing mainStateName:', {
        id,
        keys: Object.keys(routeExecution)
      });
    }
  } catch (e) {
    // ignore
  }
};

export const getRouteExecutionStatusLabel = (routeStatus, completedReason?) => {
  const intl = appIntl();
  let routeStatusLabel = '';
  let routeStatusLabelInfo = '';
  switch (routeStatus) {
    case 1:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.atOrigin'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.atOrigin'
      });
      break;
    case 2:
      routeStatusLabel =
        completedReason === null
          ? intl.formatMessage({
              id: 'routes.atDestination'
            })
          : `${intl.formatMessage({
              id: 'routes.atDestination'
            })} (Manual)`;
      routeStatusLabelInfo =
        completedReason === null
          ? intl.formatMessage({
              id: 'routes.atDestination'
            })
          : `${intl.formatMessage({
              id: 'routes.atDestination'
            })} (Manual)`;
      break;
    case 3:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.onRoute'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.onRoute'
      });
      break;
    case 4:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.unauthorizedStop'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.unauthorizedStop'
      });
      break;
    case 5:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.paused'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.paused'
      });
      break;
    case 6:
      routeStatusLabel =
        completedReason === null
          ? intl.formatMessage({
              id: 'routes.finalized'
            })
          : `${intl.formatMessage({
              id: 'routes.finalized'
            })} (Manual)`;
      routeStatusLabelInfo =
        completedReason === null
          ? intl.formatMessage({
              id: 'routes.finalized'
            })
          : `${intl.formatMessage({
              id: 'routes.finalized'
            })} (Manual)`;
      break;
    case 7:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.offRoute'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.offRoute'
      });
      break;
    case 8:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.canceled'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.canceled'
      });
      break;
    case 10:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.atCheckpoint'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.atCheckpoint'
      });
      break;
    case 15:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.eao'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.eaoInfo'
      });
      break;
    case 16:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.unauthorizedReturn'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.unauthorizedReturn'
      });
      break;
    case 18:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.singleScheduled'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.singleScheduled'
      });
      break;
    case 19:
      routeStatusLabel = intl.formatMessage({
        id: 'routes.exitOrigin'
      });
      routeStatusLabelInfo = intl.formatMessage({
        id: 'routes.exitOrigin'
      });
      break;
    default:
      routeStatusLabel = '';
      routeStatusLabelInfo = '';
      break;
  }
  return { routeStatusLabel, routeStatusLabelInfo };
};

export const completionTypes = [
  {
    value: 0,
    label: 'routes.finalizeManual'
  },
  {
    value: 1,
    label: 'routes.finalize15mins'
  },
  {
    value: 2,
    label: 'routes.finalizeInmediately'
  }
];

export const globalNotificationFrequencies = [
  {
    value: '15',
    label: 'routes.frequency15mins'
  },
  {
    value: '30',
    label: 'routes.frequency30mins'
  },
  {
    value: '60',
    label: 'routes.frequency1hour'
  },
  {
    value: '120',
    label: 'routes.frequency2hours'
  },
  {
    value: '360',
    label: 'routes.frequency6hours'
  },
  {
    value: '720',
    label: 'routes.frequency12hours'
  }
];

export const startTimeWindow = [
  {
    value: '0', // Start time window of 0 seconds (immediate start)
    label: '0 ' // Label for the start time window (in minutes)
  },
  {
    value: '900', // Start time window of 900 seconds (15 minutes)
    label: '15 ' // Label for the start time window (in minutes)
  },
  {
    value: '1800', // Start time window of 1800 seconds (30 minutes)
    label: '30 ' // Label for the start time window (in minutes)
  },
  {
    value: '2700', // Start time window of 2700 seconds (45 minutes)
    label: '45 ' // Label for the start time window (in minutes)
  },
  {
    value: '3600', // Start time window of 3600 seconds (60 minutes)
    label: '60 ' // Label for the start time window (in minutes)
  },
  {
    value: '7200', // Start time window of 7200 seconds (120 minutes)
    label: '120 ' // Label for the start time window (in minutes)
  },
  {
    value: '10800', // Start time window of 10800 seconds (180 minutes)
    label: '180 ' // Label for the start time window (in minutes)
  }
];

export const routesEvents = [
  { value: '1', label: 'routes.atOrigin' },
  { value: '3', label: 'routes.onRoute' },
  { value: '2', label: 'routes.singleCompleted' },
  { value: '6', label: 'routes.singleFinalized' },
  { value: '7', label: 'routes.offRoute' },
  { value: '4', label: 'routes.unauthorizedStop' },
  { value: '9', label: 'routes.speeding' },
  { value: '10', label: 'routes.atCheckpoint' },
  { value: '16', label: 'routes.unauthorizedReturn' },
  { value: '18', label: 'routes.singleScheduled' },
  { value: '19', label: 'routes.exitOrigin' }
];

export const routesTimelineEvents = [
  { value: '1', label: 'routes.atOrigin', color: '#44abff' },
  { value: '2', label: 'routes.singleCompleted', color: '#00d57e' },
  { value: '3', label: 'routes.onRoute', color: '#00ead5' },
  { value: '4', label: 'routes.unauthorizedStop', color: '#fdac34' },
  { value: '5', label: 'routes.paused', color: '#e3e3e4' },
  { value: '6', label: 'routes.singleFinalized', color: '#2ed573' },
  { value: '7', label: 'routes.offRoute', color: '#ff4459' },
  { value: '8', label: 'routes.canceled', color: '#ff4757' },
  { value: '9', label: 'routes.speeding', color: '#ff7a00' }, //Pendiente
  { value: '10', label: 'routes.atCheckpoint', color: '#7669e8' },
  { value: '15', label: 'routes.eao', color: '#490002' },
  { value: '16', label: 'routes.unauthorizedReturn', color: '#ba0000' },
  { value: '18', label: 'routes.singleScheduled', color: '#ffa502' },
  { value: '19', label: 'routes.exitOrigin', color: '#1dcfe7' }
];

export const routesCheckpointsIcons = [
  {
    value: '1',
    label: 'routes.checkpoint',
    icon: <Square size={20} />
  },
  {
    value: '2',
    label: 'routes.gate',
    icon: <Icon size={20} icon="Gate" />,
    iconTxt: 'Gate'
  },
  {
    value: '3',
    label: 'routes.patrol',
    icon: <Icon size={20} icon="Patrol" />,
    iconTxt: 'Patrol'
  },
  {
    value: '4',
    label: 'routes.motorcycle',
    icon: <Icon size={20} icon="Motorcycle" />,
    iconTxt: 'Motorcycle'
  },
  {
    value: '5',
    label: 'routes.frontier',
    icon: <Icon size={20} icon="Frontier" />,
    iconTxt: 'Frontier'
  },
  {
    value: '6',
    label: 'routes.carLot',
    icon: <Icon size={20} icon="CarLot" />,
    iconTxt: 'CarLot'
  }
];

export const getRouteStatusColorClass = (routeStatus: number): string => {
  if (isNaNOrNullable(routeStatus)) return '';
  switch (routeStatus) {
    case -1: //Comentario
      return 'light-comment';
    case 1:
      return 'light-onOrigin';
    case 2:
      return 'light-onDestination';
    case 3:
      return 'light-onRoute';
    case 4:
      return 'light-unauthorizedStop';
    case 5:
      return 'light-route-paused';
    case 6:
      return 'light-route-finalized';
    case 7:
      return 'light-offRoute';
    case 8:
      return 'light-route-canceled';
    case 9:
      return 'light-route-speeding';
    case 10:
      return 'light-atCheckpoint';
    case 15:
      return 'light-eao';
    case 16:
      return 'light-unauthorizedReturn';
    case 18:
      return 'light-scheduled';
    case 19:
      return 'light-exitOrigin';
  }

  return '';
};

export const getRouteStatusColorHex = (routeStatus: number): string => {
  if (isNaNOrNullable(routeStatus)) return '';
  switch (routeStatus) {
    case -1: //Comentario
      return '#0000ff';
    case 1: // En origen
      return '#44abff';
    case 2: // En destino
      return '#00d57e';
    case 3: // En ruta
      return '#00ead5';
    case 4: // Parada no autorizada
      return '#fdac34';
    case 5: // Ruta pausada
      return '#929292';
    case 6: // Ruta finalizada
      return '#2ed573';
    case 7: // Fuera de ruta
      return '#ff4459';
    case 8: // Ruta cancelada
      return '#ff4757';
    case 9: // Exceso de velocidad
      return '#ff7a00';
    case 10: // En checkpoint
      return '#7669e8';
    case 15: // EAO
      return '#490002';
    case 16: // Retorno no autorizado
      return '#ba0000';
    case 18: // Programada
      return '#ffa502';
    case 19: // Salida de origen
      return '#1dcfe7';
  }

  return '';
};

export const getMarker = (type, index, iconId, latLng) => {
  return L.marker(latLng, {
    icon: getIcon(type, index, iconId)
  });
};

export const getIcon = (
  type,
  index,
  iconId,
  checkpointClassDefaultColor = '',
  checkpointTextDefaultColor = ''
) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html:
      type === 1
        ? `<div  class='marker-pin' style='background-color:${
            checkpointClassDefaultColor || '$marker-color'
          }'></div><i class='icon-Origin' style='width: 20px;
            font-size: 20px;color:${checkpointTextDefaultColor || 'white'};'></i>`
        : type === 3
          ? `<div  class='marker-pin' style='background-color:${
              checkpointClassDefaultColor || '$marker-color'
            }'></div><i class='icon-Destination' style='width: 20px;
            font-size: 20px;color:${checkpointTextDefaultColor || 'white'};'></i>`
          : iconId == null || iconId == 1 || iconId == 0
            ? `<div  class='marker-pin' style='background-color:${
                checkpointClassDefaultColor || '$marker-color'
              }'></div><i style='width: 100%;
            font-size: 16px;color:${
              checkpointTextDefaultColor || 'white'
            };padding-right:1px'>${index}</i>`
            : `<div  class='marker-pin' style='background-color:${
                checkpointClassDefaultColor || '$marker-color'
              }'></div><i class='icon-${routesCheckpointsIcons[iconId - 1].iconTxt}' style='width: 20px;
            font-size: 20px;color:${checkpointTextDefaultColor || 'white'};'></i>`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -35]
  });
};

export const getRouteBounds = (route) => {
  if (
    route.checkpoints &&
    route.checkpoints.filter((chk) => chk.latitude != null && chk.longitude != null).length > 0
  ) {
    var latlngPolylines: any[] = [];
    if (route.checkpoints.filter((chk) => chk.segmentPath).length > 0)
      route.checkpoints.map(
        (chk) =>
          chk.segmentPath &&
          chk.segmentPath.map((path) =>
            latlngPolylines.push(L.latLng(path.latitude, path.longitude))
          )
      );

    return [
      ...route.checkpoints
        .filter((chk) => chk.latitude != null && chk.longitude != null)
        .map((chk) => L.latLng(chk.latitude, chk.longitude)),
      ...latlngPolylines
    ];
  } else return [];
};
export const getImportedRouteStatusColorClass = (success) => {
  return success ? 'light-success' : 'light-danger';
};

type RouteStatus = {
  expectedPerformance: number;
  performanceOnRoute: number;
};
export function performanceTemplate(args: RouteStatus, performanceUnit?: string) {
  const intl: IntlShape = appIntl();

  const performanceRanges: [number, number, number, number] = [0, 80, 90, 110];
  const expectedPerformance = args['expectedPerformance'];
  const value = args['performanceOnRoute'] || 0;
  const percentage = (value / expectedPerformance) * 100;

  const color = getPerformanceColor(percentage, expectedPerformance, performanceRanges);
  const translation = getPerformanceTitle(percentage, performanceRanges);

  const valueNodeRef = useRef<any>(undefined);

  return (
    <p className="d-flex align-items-center justify-content-center">
      <span
        className="font-weight-bold m-0 cursor-default"
        ref={valueNodeRef}
        style={{ color: color }}
      >
        {value?.toFixed(2) ?? ''}
      </span>
      {performanceUnit ? <span style={{ marginLeft: '4px' }}>{performanceUnit}</span> : ''}
      <UncontrolledTooltip placement={'top'} target={valueNodeRef}>
        {`${translation ? intl.formatMessage({ id: translation }) : ''} - ${percentage.toFixed(
          2
        )}%`}
      </UncontrolledTooltip>
      {/* <Icon icon={'PreventiveNotice'} className={'mr-25'} size={15} color={'primary'} /> */}
    </p>
  );
}

export interface ValidationError {
  type?: string;
  payload?: any;
  level?: string;
}

export const VALIDATION_ERROR_TYPES = {
  ROUTE_ALREADY_ASSIGNED_AND_ACTIVE_ON_DATABASE: 'routes.routeAlreadyAssignedAndActiveOnDatabase',
  ROUTE_ALREADY_ASSIGNED_AT_SAME_HOUR_THAT_ANOTHER_ON_DATABASE:
    'routes.routeAlreadyAssignedAtSameHourThatAnotherOnDatabase',
  ROUTE_ALREADY_ASSIGNED_AT_SAME_HOUR_THAT_ANOTHER_ON_STATE:
    'routes.routeAlreadyAssignedAtSameHourThatAnotherOnState',
  ROUTE_ALREADY_ASSIGNED_ON_STATE: 'routes.routeAlreadyAssignedOnState'
};

export const getValidationErrorIcon = (errors?: ValidationError[]) => {
  if (!errors || errors.length === 0) return 'CheckCircle';
  if (errors.find((error) => error.level === 'error')) return 'AlertCircle';
  return 'PreventiveNotice';
};

export const getValidationErrorColor = (errors?: ValidationError[]) => {
  if (!errors || errors.length === 0) return 'success';
  if (errors.find((error) => error.level === 'error')) return 'danger';
  return 'warning';
};

const validateDateTimesDiff = (
  firstDate: moment.Moment,
  secondDate: moment.Moment,
  diffInMilliseconds?: string
) => {
  const diffInMillisecondsAsInt = parseInt(diffInMilliseconds || '0');
  return (
    firstDate.isSame(secondDate, 'day') &&
    Math.abs(firstDate.diff(secondDate, 'ms')) < diffInMillisecondsAsInt
  );
};

// Validator that needs assigned units, and then returns a function that receives the unitsRoutesAssignment to validate
export const duplicatedUnitRouteAssignmentValidator =
  (
    assignedUnitsFromDatabase: AssignedUnit[],
    formValues: Partial<AssignmentFormValues>,
    _legacy: Partial<AssignmentFormValues>, // kept for backward compatibility
    schedule: Schedule
  ) =>
  (
    unitsRoutesAssignmentsFromState:
      | UnitsRoutesAssignment[]
      | Partial<Assignment>[]
      | Partial<Assignment>[],
    unitsRoutesAssignment: UnitsRoutesAssignment | Partial<Assignment> | Partial<Assignment>
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    const { startDate } = formValues;
    const { unitId, routeId, correlative, routeName } = unitsRoutesAssignment;
    const { assignmentType: assignmentTypeNumber } = formValues;
    // Helper function to check for duplicate assignments at the same hour
    // TODO: In the future, we must provide a smart solution to calculate the min diff time for assignments by unit (data analysis)
    const hasAssignmentAtSameHourOrScheduleNextRun = (
      existingAssignments:
        | UnitsRoutesAssignment[]
        | Partial<AssignmentByDate>[]
        | Partial<AssignmentBySchedule>[]
    ) => {
      let comparePredicate: (existingAssignment: any) => boolean = () => false;
      if (assignmentTypeNumber === AssignmentTypes.ByDate) {
        const stepperStartDate = moment(startDate, 'YYYY-MM-DD HH:mm');
        const assignmentStartDate = moment(unitsRoutesAssignment.startDate, 'YYYY-MM-DDTHH:mm:ss');
        // First remove current assign on filter, and then check if there is any other assignment with the same hour
        comparePredicate = (existingAssignment) => {
          // Si no hay startDate en el assignment que se está validando, no hay overlap
          if (!unitsRoutesAssignment.startDate && !startDate) {
            return false;
          }

          // Si el existingAssignment no tiene startDate, no puede haber overlap
          if (!existingAssignment.startDate) {
            return false;
          }

          const existingAssignmentStartDate = moment(
            existingAssignment.startDate,
            'YYYY-MM-DDTHH:mm:ss'
          );

          return validateDateTimesDiff(
            unitsRoutesAssignment.startDate ? assignmentStartDate : stepperStartDate,
            existingAssignmentStartDate,
            env.VITE_MIN_VALID_DIFF_TIME_BETWEEN_UNITS_ROUTES_ASSIGNMENT
          );
        };
      } else {
        if (!schedule || !schedule.nextRunDate) {
          return [];
        }
        const stepperNextRunDate = moment(schedule.nextRunDate, 'YYYY-MM-DD HH:mm');
        // First remove current assign on filter, and then check if there is any other assignment with the same next run date
        comparePredicate = (existingAssignment) => {
          // Si no hay scheduleNextRunDate en el assignment que se está validando, no hay overlap
          if (
            !('scheduleNextRunDate' in unitsRoutesAssignment) ||
            !unitsRoutesAssignment.scheduleNextRunDate
          ) {
            return false;
          }

          // Si el existingAssignment no tiene nextRunDate, no puede haber overlap
          if (!('nextRunDate' in existingAssignment) || !existingAssignment.nextRunDate) {
            return false;
          }

          const existingAssignmentNextRunDate = moment(
            existingAssignment.nextRunDate,
            'YYYY-MM-DDTHH:mm:ss'
          );
          const unitRoutAssignmentNextRunDate = moment(
            unitsRoutesAssignment.scheduleNextRunDate,
            'YYYY-MM-DDTHH:mm:ss'
          );

          return validateDateTimesDiff(
            unitRoutAssignmentNextRunDate,
            existingAssignmentNextRunDate,
            env.VITE_MIN_VALID_DIFF_TIME_BETWEEN_UNITS_ROUTES_ASSIGNMENT
          );
        };
      }

      // Remember: existingAssignments can be data from database or from state!
      return existingAssignments
        .filter(
          (existingAssignment) =>
            existingAssignment.unitId === unitId &&
            (existingAssignment.correlative ? existingAssignment.correlative !== correlative : true)
        )
        .some(comparePredicate);
    };

    // CASE 1: DATABASE DATA VALIDATION
    // Note that database data retrieved is filtered by active units/routes, so there is no need to validate startDate (at this moment)
    if (assignedUnitsFromDatabase.length > 0) {
      // CASE 1.1: unit has been already assigned to that route and has an active status
      // Solo buscar asignaciones de la misma unidad Y la misma ruta
      const sameRouteAssignments = assignedUnitsFromDatabase.filter(
        (unitRouteAssignment) =>
          unitRouteAssignment.unitId === unitId && unitRouteAssignment.routeId === routeId
      );

      const hasSameAssignmentFoundOnDatabase =
        sameRouteAssignments.length > 0 &&
        hasAssignmentAtSameHourOrScheduleNextRun(sameRouteAssignments);

      if (hasSameAssignmentFoundOnDatabase) {
        errors.push({
          type: VALIDATION_ERROR_TYPES.ROUTE_ALREADY_ASSIGNED_AND_ACTIVE_ON_DATABASE,
          payload: { route: routeName },
          level: 'warning'
        });
        return errors;
      }

      // CASE 1.2: a route already assigned to the unit with the same hour as the new assignment
      // Aquí buscar asignaciones de la misma unidad en CUALQUIER ruta
      const hasAssignmentFoundAtSameHourOnDatabase =
        hasAssignmentAtSameHourOrScheduleNextRun(assignedUnitsFromDatabase);
      if (hasAssignmentFoundAtSameHourOnDatabase) {
        errors.push({
          type: VALIDATION_ERROR_TYPES.ROUTE_ALREADY_ASSIGNED_AT_SAME_HOUR_THAT_ANOTHER_ON_DATABASE
        });
        return errors;
      }
    }

    // CASE 2: HOOK FORM STATE DATA VALIDATION
    // There is no need to validate startDate at this moment
    if (unitsRoutesAssignmentsFromState.length > 0) {
      // CASE 2.1: unit has been already assigned to the same route and has an active status
      const hasSameAssignmentFoundOnState = unitsRoutesAssignmentsFromState.some(
        (unitRouteAssignment) =>
          unitRouteAssignment.unitId === unitId &&
          unitRouteAssignment.routeId === routeId &&
          unitRouteAssignment.correlative !== correlative
      );
      if (hasSameAssignmentFoundOnState) {
        errors.push({ type: VALIDATION_ERROR_TYPES.ROUTE_ALREADY_ASSIGNED_ON_STATE });
        return errors;
      }

      // CASE 2.2: a route already assigned to the unit with the same hour as the new assignment
      const hasAssignmentFoundAtSameHourOnState = hasAssignmentAtSameHourOrScheduleNextRun(
        unitsRoutesAssignmentsFromState
      );
      if (hasAssignmentFoundAtSameHourOnState) {
        errors.push({
          type: VALIDATION_ERROR_TYPES.ROUTE_ALREADY_ASSIGNED_AT_SAME_HOUR_THAT_ANOTHER_ON_STATE
        });
        return errors;
      }
    }

    return errors;
  };

export const getRouteInformationFormat = (state, route) => {
  if (route) {
    return {
      ...route,
      distanceFormat: authSelectors
        .getParsers(state)
        .convertMetersToAuthUserDistanceSystem(route.distance)
        .toFixed(2),
      speedLimitFormat: authSelectors
        .getParsers(state)
        .convertKilometersPerHourToAuthUserSpeedSystem(route.speedLimit)
    };
  } else undefined;
};

export const getRouteUnitFormat = (state, unit) => {
  const parsers = authSelectors.getParsers(state);
  const {
    convertKilometersToAuthUserDistanceSystem,
    convertMetersToAuthUserDistanceSystem,
    convertLitersToAuthUserCapacitySystem,
    convertKilometersPerLiterToAuthUserFuelSystem
  } = parsers;
  return {
    ...unit,
    realDistanceLabel: convertKilometersToAuthUserDistanceSystem(unit.realDistance || 0),
    calculatedDistanceLabel: convertMetersToAuthUserDistanceSystem(unit.calculatedDistance || 0),
    effectivenessLabel: (unit.effectiveness || 0).toString() + '%',
    fuelConsumption: convertLitersToAuthUserCapacitySystem(unit.fuelConsumption || 0),
    expectedPerformance: convertKilometersPerLiterToAuthUserFuelSystem(
      unit.expectedPerformance || 0
    ),
    performanceOnRoute: convertKilometersPerLiterToAuthUserFuelSystem(unit.performanceOnRoute || 0)
  };
};

export const getRoutesRouteInformationFormat = (state, route) => {
  return {
    ...route,
    realDistanceLabel: authSelectors
      .getParsers(state)
      .convertMetersToAuthUserDistanceSystem(route.realDistance || 0),
    calculatedDistanceLabel: authSelectors
      .getParsers(state)
      .convertMetersToAuthUserDistanceSystem(route.calculatedDistance || 0),
    fuelConsumptionLabel: authSelectors
      .getParsers(state)
      .convertLitersToAuthUserCapacitySystem(route.fuelConsumption || 0),
    effectivenessLabel: (route.effectiveness || 0).toString() + '%'
  };
};

export const getRouteExecutionInformationFormat = (state, routeExecution) => {
  const intl = appIntl();
  const parsers = authSelectors.getParsers(state);

  const { routeStatusLabel, routeStatusLabelInfo } = getRouteExecutionStatusLabel(
    routeExecution.routeStatus
  );

  const {
    convertKilometersPerHourToAuthUserSpeedSystem,
    convertKilometersToAuthUserDistanceSystem,
    convertMetersToAuthUserDistanceSystem,
    convertLitersToAuthUserCapacitySystem,
    convertKilometersPerLiterToAuthUserFuelSystem
  } = parsers;

  return {
    ...routeExecution,
    routeStatusLabel,
    routeStatusLabelInfo,
    realDistanceLabel: authSelectors
      .getParsers(state)
      .convertKilometersToAuthUserDistanceSystem(routeExecution.realDistance || 0),
    calculatedDistanceLabel: authSelectors
      .getParsers(state)
      .convertMetersToAuthUserDistanceSystem(routeExecution.calculatedDistance || 0),
    effectivenessLabel: (routeExecution.effectiveness || 0).toString() + '%',
    durationLabel: routeExecution.startDate
      ? getDatesDifferenceString(
          routeExecution.startDate,
          routeExecution.completionDate ?? routeExecution.cancelDate ?? new Date(),
          intl
        )
      : 'N/A',
    transitDurationLabel: routeExecution.transitDate
      ? getDatesDifferenceString(
          routeExecution.transitDate,
          routeExecution.completionDate ?? routeExecution.cancelDate ?? new Date(),
          intl
        )
      : 'N/A',
    fuelConsumption: convertLitersToAuthUserCapacitySystem(routeExecution.fuelConsumption || 0),
    expectedPerformance: convertKilometersPerLiterToAuthUserFuelSystem(
      routeExecution.expectedPerformance || 0
    ),
    performanceOnRoute: convertKilometersPerLiterToAuthUserFuelSystem(
      routeExecution.performanceOnRoute || 0
    ),
    distanceProgress: convertKilometersToAuthUserDistanceSystem(
      routeExecution.distanceProgress || 0
    )
  };
};
